import { useContext, useEffect, useMemo, useState } from 'react';

import { LayerContext } from 'state/LayerContext';
import { useAppSelector } from 'state/hooks';
import {
  selectAnalysisSegments,
  selectODUIMode,
  useLazyGetSegmentGeometriesQuery,
} from 'state/originDestinationSlices';

import { ODUIMode } from 'appConstants';

export function useUnavailableODSegments() {
  const { layer } = useContext(LayerContext);
  const segments = useAppSelector(selectAnalysisSegments);
  const uiMode = useAppSelector(selectODUIMode);

  const segmentIds = useMemo(
    () => (segments ?? []).map((segment) => segment.id),
    [segments],
  );

  const shouldValidate =
    uiMode === ODUIMode.Create && Boolean(layer) && segmentIds.length > 0;

  const [trigger, { isFetching }] = useLazyGetSegmentGeometriesQuery();

  const [unavailableSegmentIds, setUnavailableSegmentIds] = useState<string[]>(
    [],
  );

  useEffect(() => {
    if (!shouldValidate) {
      setUnavailableSegmentIds([]);
    }
  }, [shouldValidate]);

  // Validate only when the layer changes
  useEffect(() => {
    if (!shouldValidate) return;

    let cancelled = false;
    const idsToValidate = segmentIds;

    trigger({ layerId: layer, segIds: idsToValidate }).then((result) => {
      if (cancelled) return;

      const geometries = result.data;
      if (!geometries) return;

      const foundIds = new Set(
        geometries
          .map((feature) => feature.properties?.segment_id)
          .filter(Boolean),
      );
      setUnavailableSegmentIds(idsToValidate.filter((id) => !foundIds.has(id)));
    });

    return () => {
      cancelled = true;
    };
  }, [layer, trigger]);

  useEffect(() => {
    setUnavailableSegmentIds((current) =>
      current.filter((id) => segmentIds.includes(id)),
    );
  }, [segmentIds]);

  return {
    unavailableSegmentIds,
    hasUnavailableSegments: unavailableSegmentIds.length > 0,
    isValidatingSegments: shouldValidate && isFetching,
  };
}
